import Layout from '@Components/layout'
import LayoutWithSpacing from '@Components/layout/layoutWithSpacing'
import Banner from '@Components/banner'

import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { NavbarResponse, getNavbar } from '../../apis/navbar'

import Typography from '@Components/typo'
import { CarouselContainer } from '@Components/carousel'
import { CarouselItem } from '@Components/ui/carousel'
import CardWithText from '@Components/cardwithText'
import { BannerCardResponse } from '../../apis/product/productBusinessPages'
import { WhyUsPageResponse, whyUsPage } from '../../apis/whyUsPage'
import { chunkArray } from '../../utils/chunkArray'
import { useScreenSize } from '../../hooks/useScreenSize'
import Testimonial from '@Components/sections/testimonial'
import Image from 'next/image'
import { FooterResponse, getFooter } from '../../apis/footer'
import { Seo } from '@Components/seo'

interface LandingPageProps {
  navbar: NavbarResponse
  footer: FooterResponse
  whyUsData: {
    base: WhyUsPageResponse
  }
}
const WhyUsPage = ({ whyUsData, navbar, footer }: LandingPageProps) => {
  const { base } = whyUsData
  const { isMobile, isTablet } = useScreenSize()
  const seperateDataTrustByClient = isMobile ? 1 : isTablet ? 2 : 3
  const chunkedDataTrustByClient = chunkArray(
    base.TrustedByClients,
    seperateDataTrustByClient
  )
  const pageCountTrustByClient = Math.ceil(
    base.TrustedByClients?.length / seperateDataTrustByClient
  )

  const seperateClient = isMobile ? 6 : isTablet ? 12 : 16
  const pageCountClient = Math.ceil(base.Clients.length / seperateClient)
  const chunkedDataClient = chunkArray(base.Clients, seperateClient)

  return (
    <>
      <Seo
        title={base?.Seo?.MetaTitle || 'Why Us | On-ion website'}
        description={base?.Seo?.MetaDescription || ''}
        keywords={base?.Seo?.Keywords || ''}
        canonicalURL={base?.Seo?.CanonicalURL || ''}
      />
      <Layout
        navbar={navbar}
        footer={footer}
        className='bg-neutral-level-2-default'
      >
        <div className='w-full'>
          <Banner
            title={base.BannerTitle}
            bannerImage={base.BannerImage.url}
            size='md'
          />
        </div>

        <LayoutWithSpacing>
          <div className='flex flex-col gap-8'>
            <div className='flex flex-col gap-16'>
              <div className='flex w-full flex-col gap-8'>
                <Typography size='h4' weight='bold'>
                  {base.OurCoreBusinessTitle}
                </Typography>
                <div className='flex flex-col gap-8 lg:flex-row'>
                  {base.OurCoreBusiness.map((item, index) => (
                    <div
                      key={index}
                      className='flex w-full flex-col gap-4 lg:w-[33%]'
                    >
                      <div>
                        <Image
                          src={item.Image?.url}
                          width={100}
                          height={100}
                          alt=''
                          className='h-full w-full rounded-2xl'
                        />
                      </div>
                      <div className='flex flex-col gap-2.5'>
                        <Typography size='h6' weight='bold'>
                          {item.Title}
                        </Typography>
                        <Typography>{item.Content}</Typography>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </LayoutWithSpacing>

        <div className='w-full bg-content-default'>
          <LayoutWithSpacing className='flex flex-col gap-y-8'>
            <div className='text-[1.563rem] font-bold text-content-on-color-background'>
              {base.HighlightTitle}
            </div>

            <div className='flex flex-col gap-8 lg:flex-row'>
              {base.Highlights.map((item, index) => (
                <div
                  key={index}
                  className='flex w-full flex-row gap-4 lg:w-[33%]'
                >
                  <div className='flex w-[74px]'>
                    <Image
                      src={item.Icon?.url}
                      alt=''
                      width={100}
                      height={100}
                      className='h-[74px] w-[74px] min-w-[74px] p-4'
                    />
                  </div>
                  <div className='flex flex-col gap-2'>
                    <Typography size='h6' weight='bold' className='text-white'>
                      {item.Title}
                    </Typography>
                    <Typography className='text-white'>
                      {item.Content}
                    </Typography>
                  </div>
                </div>
              ))}
            </div>
          </LayoutWithSpacing>
        </div>

        <LayoutWithSpacing>
          <div className='flex flex-col gap-8'>
            <div className='flex flex-col gap-6'>
              <Typography size='h4' weight='bold'>
                {base.OurGrowthTitle}
              </Typography>
              <div className='flex flex-col gap-8 lg:flex-row'>
                <div className='flex w-full flex-col gap-8 lg:w-1/2'>
                  <Typography>{base.OurGrowthContent}</Typography>
                  <div className='flex w-full flex-wrap justify-between gap-8'>
                    {base.Statistics.sort((a, b) => a.Order - b.Order).map(
                      (item, index) => {
                        return (
                          <div
                            key={index}
                            className='flex basis-full flex-col gap-y-3 rounded-2xl bg-neutral-level-1-default p-9 text-content-default lg:basis-[46%]'
                          >
                            <div className='flex flex-row gap-x-3'>
                              <Image
                                className='h-8 w-8'
                                src={item.Icon?.url}
                                alt=''
                                width={100}
                                height={100}
                              />
                              <div className='truncate text-[1.938rem] font-bold'>
                                {item.Value}
                              </div>
                            </div>
                            <Typography
                              size='large'
                              className='max-w-full truncate lg:max-w-[140px]'
                            >
                              {item.Title}
                            </Typography>
                          </div>
                        )
                      }
                    )}
                  </div>
                </div>

                <div className='flex w-full justify-center lg:w-1/2'>
                  <Image
                    src={base.OurGrowthImage.url}
                    alt=''
                    width={100}
                    height={100}
                    className='h-[604px] w-[364px]'
                  />
                </div>
              </div>
            </div>
          </div>
        </LayoutWithSpacing>

        <div className='w-full bg-neutral-level-1-default'>
          <LayoutWithSpacing>
            <div className='flex flex-col gap-8'>
              <div className='flex w-full flex-col gap-16'>
                {chunkedDataTrustByClient.length > 0 && (
                  <div className='flex flex-col gap-8'>
                    <Typography size='h4' weight='bold'>
                      {base.TrustedByClientTitle}
                    </Typography>
                    <CarouselContainer
                      pageCount={chunkedDataTrustByClient.length}
                      dataLength={pageCountTrustByClient}
                      customClass='flex flex-col gap-8'
                    >
                      {Array.from({ length: pageCountTrustByClient }).map(
                        (_, index) => (
                          <CarouselItem
                            key={index}
                            className='flex flex-row justify-between gap-8'
                          >
                            {chunkedDataTrustByClient[index]?.map(
                              (item: BannerCardResponse, dataIndex: number) => (
                                <div
                                  key={dataIndex}
                                  className={`${isMobile ? 'w-full' : 'w-1/2'}`}
                                >
                                  <CardWithText
                                    title={item.Title}
                                    subTitle={item.SubTitle}
                                    imagePath={item.Image.url}
                                    isSeperate
                                  />
                                </div>
                              )
                            )}
                          </CarouselItem>
                        )
                      )}
                    </CarouselContainer>
                  </div>
                )}
              </div>
              <div>
                <CarouselContainer
                  pageCount={pageCountClient}
                  dataLength={chunkedDataClient.length}
                >
                  {Array.from({ length: pageCountClient }).map((_, index) => (
                    <CarouselItem key={index} className='w-full'>
                      <div className='flex h-full min-h-[13.75rem]'>
                        <div className='flex w-full flex-row flex-wrap gap-y-5'>
                          {chunkedDataClient[index]?.map(
                            (item: any, index: number) => (
                              <div
                                key={`${item.id}-${index}`}
                                className={`lg:justify-left flex items-start ${
                                  isMobile
                                    ? 'basis-[33%]'
                                    : isTablet
                                      ? 'basis-[16.6%]'
                                      : 'basis-[12.5%]'
                                }`}
                              >
                                <Image
                                  width={100}
                                  height={100}
                                  src={item.Logo?.url}
                                  alt=''
                                />
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContainer>
              </div>
            </div>
          </LayoutWithSpacing>
        </div>

        <div>
          {base.Testimonials?.length > 0 && (
            <Testimonial
              data={{
                Title: base.TestimonialTitle,
                Contents: base.Testimonials,
              }}
            />
          )}
        </div>
      </Layout>
    </>
  )
}

export default WhyUsPage

export const getServerSideProps = async ({
  locale = 'en',
}: {
  locale: string
}) => {
  const { getWhyUsPage } = whyUsPage
  const [navbar, base, footer] = await Promise.all([
    getNavbar({ locale }),
    getWhyUsPage({ locale }),
    getFooter({ locale }),
  ])
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      navbar: navbar[0],
      footer: footer[0],
      whyUsData: {
        base: base[0],
        OurCoreBusiness: base[0].OurCoreBusiness.sort(
          (a, b) => a.Order - b.Order
        ),
        Clients: base[0].Clients.sort((a, b) => a.Order - b.Order),
        Highlights: base[0].Highlights.sort((a, b) => a.Order - b.Order),
        Statistics: base[0].Statistics.filter(
          (item) => item.Section === 'why-us'
        ),
      },
    },
  }
}
